import { createContext, useContext } from 'react';
import {
  useCreateProductSetForBudgetsPurchaseForHirerMutation,
  useGetProductSetQuery,
} from 'src/App/generated/graphql-types';
import { getLogger } from 'src/App/utils/logger/logger';
import { CheckoutActionType } from './reducer';
import {
  type AdBudgetProductItemType,
  mapAdBudgetInfo,
} from '../mapAdBudgetInfo';
import { AppError } from 'src/App/utils/error';
import { useStore } from './provider';
import { useLocation } from 'react-router-dom';
import { useAppContext } from 'src/App/context/AppContext';
import { getQueryParam } from '@seek/uiv-banner';

interface ProductSetProviderProps {}
interface ProductSetContextProps {
  availableProductSetId: string;
  availableProductId: string;
}

const ProductSetContext = createContext<ProductSetContextProps | null>(null);

export const ProductSetProvider: React.FC<
  React.PropsWithChildren<ProductSetProviderProps>
> = ({ children }) => {
  const { dispatch } = useStore();
  const { state } = useLocation();
  const { country } = useAppContext();

  const productSetId = state?.productSetId;
  const productId = state?.productId ?? getQueryParam('productId');

  const [
    createProductSetForBudgetsPurchaseForHirer,
    { data: hirerData, called: createProductSetForHirerMutationCalled },
  ] = useCreateProductSetForBudgetsPurchaseForHirerMutation({
    onError: (error) => {
      throw new AppError('GET_PRODUCT_SET_ERROR', error.message);
    },
  });

  if (!productSetId && productId) {
    if (!createProductSetForHirerMutationCalled) {
      createProductSetForBudgetsPurchaseForHirer();
    }
  }

  const hirerProductSetId =
    hirerData?.createProductSetForBudgetsPurchaseForHirer?.productSet.id;

  const { data: getProductSetData, error: getProductSetError } =
    useGetProductSetQuery({
      variables: {
        input: {
          productSetId: productSetId! ?? hirerProductSetId!,
        },
      },
      skip: (!productSetId && !hirerProductSetId) || !productId,
      onError: (error) => {
        getLogger().error('get product set', {
          error,
          activity: 'getProductSet',
          productSetId,
          productId,
        });
      },
      onCompleted: (data) => {
        if (data?.getProductSet.__typename === 'ProductSet') {
          const productSetItems = data?.getProductSet?.items;
          const budget = data?.getProductSet?.items?.find(
            (item) => item?.id === productId,
          );

          dispatch({
            type: CheckoutActionType.SET_FETCHED_BUDGETS,
            adBudgetList: productSetItems.map((item) =>
              mapAdBudgetInfo(item as AdBudgetProductItemType, country),
            ),
          });

          if (budget) {
            dispatch({
              type: CheckoutActionType.SET_FETCHED_BUDGET,
              adBudget: mapAdBudgetInfo(
                budget as AdBudgetProductItemType,
                country,
              ),
            });
          }
        }
      },
    });

  if (getProductSetError) {
    throw new AppError('GET_PRODUCT_SET_ERROR', getProductSetError.message);
  }

  if (
    getProductSetData?.getProductSet.__typename === 'ProductSetNotFoundError'
  ) {
    throw new AppError('GET_PRODUCT_SET_ERROR', 'Product set not found');
  }

  if (
    getProductSetData?.getProductSet.__typename === 'ProductSetExpiredError'
  ) {
    throw new AppError('GET_PRODUCT_SET_ERROR', 'Product set expired');
  }

  const availableProductSetId = getProductSetData?.getProductSet.id;
  const availableProductId = getProductSetData?.getProductSet?.items?.find(
    (item) => item?.id === productId,
  )?.id;

  return (
    <ProductSetContext.Provider
      value={{
        availableProductId: availableProductId ?? '',
        availableProductSetId: availableProductSetId ?? '',
      }}
    >
      {children}
    </ProductSetContext.Provider>
  );
};

export const useProductSet = () => {
  const context = useContext(ProductSetContext);
  if (context === null) {
    throw new Error('useProductSet must be used within a ProductSetProvider');
  }

  return context;
};
